import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { CacheBucket, HttpCacheManager, withCache } from '@ngneat/cashew';
import {
	LoanPayment,
	LoanApplication,
	LoanPaymentDetail,
	StudentLoanType,
	StudentLoanPaymentHistory,
	LoanFrequency,
	LoanServicer,
	Program,
	EducationalProvider,
	LoanRepaymentStatus,
	LoanServicerDetail,
	PaymentHistory,
	ApplicationSubmission
} from '@edassist/api';
import { Operation } from 'fast-json-patch';
import { catchError } from 'rxjs/operators';
import { FormComponent } from '@edassist-ui/forms';
import { APPLICATION_STATUSES, constants, loanConstants } from '@edassist/common';
import { ActiveDirectPayLoanResponse } from '../../models/application/active-direct-pay-loan-response.interface';
import { LoanApplicationSubmissionResponse } from '../../models/loan/loan-application-submission-response.interface';

@Injectable({ providedIn: 'root' })
export class LoanService {
	applicationData: Observable<LoanApplication>;
	private readonly relativeUrl = 'participants/';
	private readonly applicationsRelativeUrl = 'applications/';

	private readonly studentLoanTypeBucket = new CacheBucket();
	private readonly loanFrequencyBucket = new CacheBucket();
	private readonly applicationDataSubject: BehaviorSubject<LoanApplication>;
	constructor(
		private readonly http: HttpClient,
		private readonly manager: HttpCacheManager
	) {
		this.applicationDataSubject = new BehaviorSubject<LoanApplication>(undefined);
		this.applicationData = this.applicationDataSubject.asObservable();
	}

	getRecentPaymentsByParticipant(participantId: number): Observable<Array<LoanPayment>> {
		return this.http.get<Array<LoanPayment>>(`student-loans/participants/${participantId}/payments`);
	}

	getLoanApplicationByApplicationId(applicationId: number): void {
		this.http
			.get<LoanApplication>(`loans/applications/${applicationId}`)
			.pipe(catchError(() => of(undefined)))
			.subscribe((application: LoanApplication) => {
				// Refresh all components who need application data:
				this.applicationDataSubject.next(application);
			});
	}

	getLoanPaymentDetails(applicationId: number): Observable<LoanPaymentDetail> {
		return this.http.get<LoanPaymentDetail>(`student-loans/applications/${applicationId}/loan-payment-detail`);
	}

	getPaymentHistory(applicationId: number): Observable<PaymentHistory> {
		const url = `${this.applicationsRelativeUrl}${applicationId}/payment-history`;

		return this.http.get<PaymentHistory>(url);
	}

	patchLoanPaymentDetails(applicationId: number, patchObject: Array<Operation>): Observable<LoanPaymentDetail> {
		return this.http.patch<LoanPaymentDetail>(
			`student-loans/applications/${applicationId}/loan-payment-detail`,
			patchObject
		);
	}

	saveLoanPaymentDetails(applicationId: number, patchObject: Array<Operation>): Observable<LoanPaymentDetail> {
		return this.http.patch<LoanPaymentDetail>(
			`student-loans/applications/${applicationId}/loan-payment-detail`,
			patchObject
		);
	}

	getStudentLoanType(resetCache?: boolean): Observable<Array<StudentLoanType>> {
		if (resetCache) {
			this.manager.delete(this.studentLoanTypeBucket);
		}

		return this.http.get<Array<StudentLoanType>>('/loan-accounts/types', {
			context: withCache({
				bucket: this.studentLoanTypeBucket
			})
		});
	}

	getLoanRepaymentStatuses(): Array<LoanRepaymentStatus> {
		return loanConstants.loanRepaymentStatuses;
	}

	getRecentPaymentsByStudent(studentId: number, records?: number): Observable<Array<StudentLoanPaymentHistory>> {
		const params = new HttpParams().set('recordLimit', records);

		return this.http.get<Array<StudentLoanPaymentHistory>>(`student-loans/${studentId}/aggregator-payments-history`, {
			params
		});
	}

	clearApplicationData(): void {
		this.applicationDataSubject.next(undefined);
	}

	isActiveDirectPayLoan(participantId: number): Observable<ActiveDirectPayLoanResponse> {
		const url = `${this.relativeUrl}${participantId}/loans`;
		const params = new HttpParams().set('active', 'true').set('programTypeCode', 'SLD').set('recordsPerPage', '5');

		return this.http.get<ActiveDirectPayLoanResponse>(url, { params });
	}

	getLoanFrequency(resetCache?: boolean): Observable<Array<LoanFrequency>> {
		if (resetCache) {
			this.manager.delete(this.loanFrequencyBucket);
		}

		return this.http.get<Array<LoanFrequency>>('/loan-accounts/frequencies', {
			context: withCache({
				bucket: this.loanFrequencyBucket
			})
		});
	}

	isMonthlyRecurring(loanFrequencies: LoanFrequency[]): boolean {
		return loanFrequencies.some((frequency) => frequency.code === constants.eligibleRequestFreq.monthly);
	}

	isOneTime(loanFrequencies: LoanFrequency[]): boolean {
		return loanFrequencies.some((frequency) => frequency.code === constants.eligibleRequestFreq.onetime);
	}

	getActiveNonInternationalLoanServicers(): Observable<Array<LoanServicer>> {
		const params = new HttpParams().set('onlyActive', 'true').set('onlyNonInternational', 'true');

		return this.http.get<Array<LoanServicer>>('/loan-servicers', { params });
	}

	getLoanServicerDetails(loanServicerId: number): Observable<Array<LoanServicerDetail>> {
		return this.http.get<Array<LoanServicerDetail>>(`loan-servicers/${loanServicerId}/detail`);
	}

	getLoanApplications(participantId: number): Observable<Array<LoanApplication>> {
		return this.http.get<Array<LoanApplication>>(`participants/${participantId}/loans`);
	}

	createLoanApplication(application: LoanApplication): Observable<LoanApplication> {
		return this.http.post<LoanApplication>(`micro-loan/applications/loans`, application);
	}

	createPaymentApplicationByAdmin(
		applicationId: number,
		requestedPaymentAmount: number,
		requestedPaymentDate: Date
	): Observable<ApplicationSubmission> {
		return this.http.post<ApplicationSubmission>(
			`micro-loan/applications/${applicationId}/ia-loan-payments`,
			undefined,
			{
				params: {
					requestedPaymentAmount: requestedPaymentAmount,
					requestedPaymentDate: requestedPaymentDate.toISOString()
				}
			}
		);
	}

	updateLoanApplication(id: string, applicationId: string, application: LoanApplication): Observable<LoanApplication> {
		const url = `micro-loan/participants/${id}/applications/loans/${applicationId}`;

		return this.http.put<LoanApplication>(url, application);
	}

	submissionLoanApplication(applicationId: string): Observable<LoanApplicationSubmissionResponse> {
		const url = `/micro-loan/applications/${applicationId}/loans/submission`;

		return this.http.put<LoanApplicationSubmissionResponse>(url, null);
	}

	submissionLoanAgreement(applicationId: string): Observable<LoanApplication> {
		const url = `applications/${applicationId}/agreements?agreementVerify=true`;

		return this.http.post<LoanApplication>(url, null);
	}

	deleteLoanApplication(participantId: string, applicationId: string, comment: string): Observable<void> {
		let params = new HttpParams();

		if (comment) {
			params = params.set('comment', comment);
		}

		return this.http.delete<void>(`micro-loan/participants/${participantId}/applications/loans/${applicationId}`, {
			params
		});
	}

	isLoanReimbursement(programTypeId: number): boolean {
		return programTypeId === 8;
	}
	
	createLoanPayment(loanPaymentApplication: LoanApplication): Observable<LoanApplicationSubmissionResponse> {
		const url = 'micro-loan/applications/loan-payments';

		return this.http.post<LoanApplicationSubmissionResponse>(url, loanPaymentApplication);
	}

	generatePaymentApp(loanApplication: LoanApplication): Observable<LoanApplication> {
		loanApplication.applicationDetail.loanRequest.isLoan = false;

		return of(loanApplication);
	}

	getLoanApplication(participantId: number, applicationId: number): Observable<LoanApplication> {
		const url = `${this.relativeUrl}${participantId}/${this.applicationsRelativeUrl}${applicationId}`;

		return this.http.get<LoanApplication>(url);
	}

	stopPayingLoanRequest(participantId: number, applicationId: number, comment: string): Observable<LoanApplication> {
		const url = `micro-loan/${this.relativeUrl}${participantId}/loans/${applicationId}/stop-paying-loan`;
		const params = new HttpParams().set('comment', comment);

		return this.http.put<LoanApplication>(url, null, { params });
	}

	stopPaymentRequest(applicationId: number, currentUserId: number, comment): Observable<LoanApplication> {
		const url = `${this.applicationsRelativeUrl}/${applicationId}/cancellation`;
		const params = new HttpParams().set('currentUserId', currentUserId);

		return this.http.post<LoanApplication>(
			url,
			{ applicationStatusCode: APPLICATION_STATUSES.cancel, comment },
			{ params }
		);
	}

	updatePaymentRequest(loanRequestId: number, applicationId: number, loanRequestedPaymentAmount: number): Observable<LoanApplication> {
		const url = `${this.applicationsRelativeUrl}${applicationId}/loan-requests/${loanRequestId}`;

		const payload = {
			id: loanRequestId,
			applicationId: applicationId,
			loanRequestedPaymentAmount: loanRequestedPaymentAmount
		};

		return this.http.put<LoanApplication>(url, payload);
	}

	generateLoanApplication(
		createApplicationForm: FormComponent,
		program: Program,
		selectedProvider: EducationalProvider,
		participantId: number,
		applicationId: number
	): LoanApplication {
		return {
			id: applicationId,
			applicationDetail: {
				otherCourseOfStudy: createApplicationForm?.value?.otherFieldOfStudy,
				courseOfStudy: createApplicationForm?.value?.fieldOfStudy,
				degreeObjective: createApplicationForm?.value?.educationProgram,
				loanRequest: {
					graduationDate: createApplicationForm?.value?.completionDate,
					isLoan: true,
					numberOfPayments: 0,
					previouslyApproved: false,
					loanRequestedPaymentAmount: createApplicationForm?.value?.requestAmount,
					studentLoan: {
						paymentFrequencyCode: createApplicationForm?.value?.loanFrequencyOptions,
						studentLoanTypeId: createApplicationForm?.value?.loanType,
						active: createApplicationForm?.value?.active,
						accountNumber: createApplicationForm?.value?.accountNumber,
						interestRate: createApplicationForm?.value?.interestRate,
						loanAggregatorUserAccountId: null,
						loanBalance: createApplicationForm?.value?.loanBalance,
						loanServicer: {
							id: createApplicationForm?.value?.loanServicer?.id,
							name: createApplicationForm?.value?.loanServicer?.name,
							yodleeProviderId: createApplicationForm?.value?.loanServicer?.yodleeProviderId
						},
						loanSourceId: 1,
						originalLoanAmount: 1111,
						participantId: participantId,
						loanRepaymentStatus: createApplicationForm?.value?.loanRepaymentStatus
					}
				},
				sessionStartDate: 0,
				sessionEndDate: 0,
				certificationStartDate: undefined,
				certificationEndDate: undefined,
				postNominalLetters: ''
			},
			benefitPeriod: {
				program: program
			},
			educationalProvider: selectedProvider,
			participantId: participantId
		};
	}

	getLoanRepaymentStatusDisplayValue(loanRepaymentStatusValue: string): string {
		let loanRepaymentStatusDisplayValue = '-';
		if (loanRepaymentStatusValue) {
			const loanRepaymentStatus = loanConstants.loanRepaymentStatuses.find(
				(status) => status.value === loanRepaymentStatusValue
			);

			if (loanRepaymentStatus) {
				loanRepaymentStatusDisplayValue = loanRepaymentStatus.display;
			} else if (
				loanRepaymentStatusValue === loanConstants.loanForbearanceStatusValue ||
				loanRepaymentStatusValue === loanConstants.loanDefermentStatusValue
			) {
				loanRepaymentStatusDisplayValue = loanConstants.loanDefermentForbearanceStatusDisplayValue;
			} else if (loanRepaymentStatusValue === loanConstants.loanDefaultedStatusValue) {
				loanRepaymentStatusDisplayValue = loanConstants.loanDefaultedStatusDisplayValue;
			}
		}

		return loanRepaymentStatusDisplayValue;
	}
}
